import { Injectable, inject } from '@angular/core';

import { Observable, ReplaySubject, catchError, of, switchMap, tap } from 'rxjs';

import { StorageService } from '@hdms/shared/util-core';

import { BannerHelperService } from './banner-helper.service';
import { CustomerJourneyGqlService } from './customer-journey-gql.service';

type TimeStampedJourneyId = [number, string];

// prefixed with 'pf' so page-frame StorageService instance will clean it up on logout
export const JOURNEY_STORAGE_KEY = 'pf-customerJourneyId';

// Journey ID expiry in minutes
const JOURNEY_ID_TTL = 30;

@Injectable({
  providedIn: 'root'
})
export class CustomerJourneyService {
  private readonly storageService = inject(StorageService);
  private readonly helper = inject(BannerHelperService);
  private readonly journeyGql = inject(CustomerJourneyGqlService);

  private readonly update = new ReplaySubject<boolean>(1);

  constructor() {
    this.listenToRouteChange();
  }

  get update$(): Observable<boolean> {
    return this.update.asObservable();
  }

  get guid(): string {
    return this.helper.canRequestBanners
      ? this.validateStoredJourneyId(this.storageService.getItem<TimeStampedJourneyId>(JOURNEY_STORAGE_KEY))
      : null;
  }

  /**
   * Save journey ID to localStorage after every `saveCustomerJourney` mutation.
   * In order to circumvent localStorage not supporting expiry, the ID will
   * be set together with a timestamp 30 mins ahead of current time.
   */
  set guid(id: string) {
    const date = new Date();
    this.storageService.setItem(JOURNEY_STORAGE_KEY, [date.setMinutes(date.getMinutes() + JOURNEY_ID_TTL), id]);
  }

  public removeGuid(): void {
    this.storageService.removeItem(JOURNEY_STORAGE_KEY);
  }

  /**
   * Save customer journey
   * - on every NavigationEnd event
   * - client-side only
   * - not in mobileApp
   * - not for account managers
   *
   * As we've to take care for late subscribers, update has to be a ReplaySubject.
   * So in order to block immediate banner loading after subscription while a
   * journey save is in progress, emit `false` before, so subscribers can filter,
   * then `true` once save request is done.
   * @private
   */
  private listenToRouteChange(): void {
    this.helper.canRequestBanners
      ? this.helper.routeChange$
          .pipe(
            tap(() => this.update.next(false)),
            switchMap(url => this.saveCustomerJourney$(url)),
            tap(() => this.update.next(true))
          )
          .subscribe()
      : this.update.next(true);
  }

  /**
   * Save customer journey and update journey GUID in session storage.
   * @param uri
   * @private
   */
  private saveCustomerJourney$(uri: string): Observable<unknown> {
    return this.journeyGql
      .mutate({
        input: {
          uri,
          journeyId: this.guid,
          productId: this.helper.productId
        }
      })
      .pipe(
        tap(({ data }) => (this.guid = data.saveCustomerJourney.journeyId)),
        catchError(() => {
          this.removeGuid();
          return of(null);
        })
      );
  }

  /**
   * Returns journey ID if timestamp is bigger than current date
   * @param data
   * @private
   */
  private validateStoredJourneyId(data: TimeStampedJourneyId): string {
    const [timestamp, id] = data || [];
    return new Date().getTime() <= timestamp ? id : null;
  }
}
